import { isRejected } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Select } from '@sgme/ui';
import { isBefore } from 'date-fns';
import { type PropsWithChildren, type RefObject, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCreateStripMutation } from '@/api/trade-capture';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker';
import { selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { IntlMessageId } from '@/types/intl';

interface StripPopupProps {
  show: boolean;
  onToggle: (nextShow: boolean) => void;
  loading?: boolean;
  target: RefObject<HTMLDivElement | null>;
}

export function StripPopup({ show, onToggle, loading, target }: StripPopupProps) {
  const [createStrip, { isError }] = useCreateStripMutation();
  const { tileId, idVersion, sessionId, expiry } = useOptionSelector((option) => {
    const tileId = option.properties.tileId;
    return {
      tileId,
      idVersion: option.tradeCapture.version,
      sessionId: option.tradeCapture.sessionId,
      expiry: selectOptionLegsProperties(option, `${tileId}/0`)?.expiryDate,
    };
  });
  const [mode, setMode] = useState<'repeat' | 'endDate'>('repeat');
  const [frequency, setFrequency] = useState<'Daily' | 'Weekly' | 'Monthly'>('Monthly');
  const [startDate, setStartDate] = useState(expiry);
  const [endDate, setEndDate] = useState('');
  const [iterations, setIterations] = useState<number | undefined>(undefined);
  const { formatMessage } = useIntl();

  type ValidatedParams =
    | { type: 'invalid' }
    | { type: 'repeat'; startDate: string; iterations: number }
    | { type: 'endDate'; startDate: string; endDate: string };

  function validateParams(): ValidatedParams {
    if (isNotDefined(startDate)) {
      return { type: 'invalid' };
    }
    if (mode === 'repeat' && isDefined(iterations)) {
      return { type: 'repeat', startDate, iterations };
    }
    if (mode === 'endDate' && isDefined(endDate) && isBefore(startDate, endDate)) {
      return { type: 'endDate', startDate, endDate };
    }
    return { type: 'invalid' };
  }

  const state = validateParams();

  async function handleConfirm() {
    if (state.type === 'repeat') {
      const { startDate, iterations } = state;
      const response = await createStrip({ tileId, idVersion, sessionId, frequency, startDate, iterations });
      onToggle(!isRejected(response));
    } else if (state.type === 'endDate') {
      const { startDate, endDate } = state;
      const response = await createStrip({ tileId, idVersion, sessionId, frequency, startDate, endDate });
      onToggle(!isRejected(response));
    }
  }

  return (
    <Overlay placement="bottom-end" show={show} target={target} onHide={() => onToggle?.(false)}>
      <Popover className="p-2">
        <Popover.Header>
          <FormattedMessage tagName="h5" id="option.strip.menuTitle" />
        </Popover.Header>

        <Popover.Body>
          <div className="d-flex flex-column gap-2">
            <FormGroup labelId="option.strip.frequency">
              <Select
                className="form-select-sm"
                selectedItem={frequency}
                itemsAsObjects={['Daily', 'Weekly', 'Monthly'] as const}
                onChange={setFrequency}
                itemToString={(item) => formatMessage({ id: `option.strip.frequency.${item}` })}
              />
            </FormGroup>

            <FormGroup labelId="option.strip.startDate">
              <SgDatePicker
                value={startDate}
                onChange={setStartDate}
                data-e2e="strip-start-date"
                className="fx-date-picker"
                size="sm"
                yearControls
                showClearButton={false}
                inputId="start-date"
              />
            </FormGroup>

            <FormGroup labelId="option.strip.iterations">
              <div className="d-flex flex-column">
                <div>
                  <div className="mb-2">
                    <Radio
                      id="strip-iteration-repeat-button"
                      checked={mode === 'repeat'}
                      onChange={() => setMode('repeat')}
                    />
                    <div className="input-group input-group-sm ">
                      <SgAmountInput
                        data-e2e="strip-iterations"
                        onChange={setIterations}
                        unit="times"
                        disabled={mode !== 'repeat'}
                      />
                    </div>
                  </div>
                  <div>
                    <Radio
                      id="strip-iterations-endDate-button"
                      checked={mode === 'endDate'}
                      onChange={() => setMode('endDate')}
                    />
                    <SgDatePicker
                      data-e2e="strip-end-date"
                      value={endDate}
                      onChange={setEndDate}
                      className="fx-date-picker"
                      size="sm"
                      yearControls
                      showClearButton={false}
                      disabled={mode !== 'endDate'}
                    />
                  </div>
                </div>
              </div>
            </FormGroup>

            <div className="d-flex gap-2 justify-content-end mb-2">
              <Button data-e2e="strip-cancel-button" onClick={() => onToggle(false)}>
                <FormattedMessage id="option.strip.cancel" />
              </Button>
              <Button
                loading={loading}
                disabled={state.type === 'invalid'}
                data-e2e="strip-confirm-button"
                purpose="primary"
                onClick={handleConfirm}
              >
                <FormattedMessage id="option.strip.create" />
              </Button>
            </div>
          </div>

          {isError && (
            <div className="mt-2 text-warning line-height-1">
              <i className="icon icon-md me-2">error_outline</i>
              <FormattedMessage id="option.strip.error" />
            </div>
          )}
        </Popover.Body>
      </Popover>
    </Overlay>
  );
}

function Radio({ checked, onChange, id }: { checked: boolean; onChange: () => void; id: string }) {
  return (
    <div className="form-check form-check-sm">
      <input type="radio" className="form-check-input" id={id} data-e2e={id} checked={checked} onChange={onChange} />
      <label htmlFor={id} className="form-check-label ms-0">
        Repeat
      </label>
    </div>
  );
}

type FormGroupProps = PropsWithChildren<{
  labelId: IntlMessageId;
}>;

function FormGroup({ labelId, children }: FormGroupProps) {
  return (
    <div className="form-group form-group-sm d-flex my-1 no-gutters">
      <div className="pe-1 col-form-label col-form-label-sm col-4 text-nowrap text-end">
        <FormattedMessage id={labelId} />
      </div>
      <div className="d-flex flex-column px-1 col-7">{children}</div>
    </div>
  );
}
